import React, { useState } from 'react';
import { useLeaderboard } from '../hooks/useLeaderboard';
import { useLeaderboardScopes } from '../hooks/useLeaderboardScopes';
import LeaderboardBoard from '../components/LeaderboardBoard';
import ScopeChips, { ScopeChipItem } from '../components/ScopeChips';
import LocalizationService from '../../application/services/LocalizationService';
import './LeaderboardPages.css';

const t = (key: string, options?: Record<string, unknown>) =>
  LocalizationService.translate(`leaderboard.${key}`, undefined, options) as string;

/** /leaderboard — global board + country selector (Web UI section). Page stays thin: all fetching
 * lives in useLeaderboard/useLeaderboardScopes, all rendering in LeaderboardBoard/ScopeChips. */
const LeaderboardPage: React.FC = () => {
  const [page, setPage] = useState(0);
  const { board, isLoading, error } = useLeaderboard('GLOBAL', null, page);
  const { scopes } = useLeaderboardScopes();

  const chips: ScopeChipItem[] = [
    { key: 'global', label: t('global'), to: '/leaderboard', active: true },
    ...(scopes?.countries ?? []).map((country) => ({
      key: country.code,
      label: country.name,
      to: `/leaderboard/${country.code.toLowerCase()}`,
    })),
  ];

  return (
    <div className="lbp-root">
      <header className="lbp-header">
        <span className="lbp-eyebrow">{t('eyebrow')}</span>
        <h1 className="lbp-headline">{t('globalHeadline')}</h1>
        <p className="lbp-sub">{t('globalSub')}</p>
      </header>

      {chips.length > 1 && (
        <div className="lbp-selector">
          <span className="lbp-selector-label">{t('jumpToCountry')}</span>
          <ScopeChips ariaLabel={t('jumpToCountry')} items={chips} />
        </div>
      )}

      {isLoading && <div className="lbp-loading">{t('loading')}</div>}
      {error && <div className="lbp-error">{error}</div>}
      {board && <LeaderboardBoard board={board} page={page} onPageChange={setPage} />}
    </div>
  );
};

export default LeaderboardPage;
